import * as React from "react";
import { FileItem, FolderItem, Item } from "@/types/fse";
import { createBreadCrumbs } from "./helpers";
import { useSortedItems } from "@/hooks/use-sorted-items";
import TopBarProgress from "react-topbar-progress-indicator";
import { DeleteConfirmationModal } from "./modals/deleteConfirmationModal";
import { useItemSelection } from "./hooks/useItemSelection";
import { useMoveDelete } from "./hooks/useMoveDelete";
import { DriveContent } from "./components/DriveContent";
import { SortOption } from "@/components/sortDropdown";
import { useSearchParams } from "react-router";

interface Props {
  totalPages: number;
  isRootDirectory: boolean;
  isLoading: boolean;
  setCurrentPage: (page: number) => void;
  currentPage: number;
  itemsPerPage: number;
  displayItems: Item[];
  error: Error | null | unknown;
  data: FileItem | FolderItem | undefined;
  folderId?: string | undefined;
  isSearchMode?: boolean;
  onRetry?: () => unknown;
}

export const DriveBasePage: React.FC<Props> = ({
  isLoading,
  isRootDirectory,
  error,
  displayItems,
  itemsPerPage,
  totalPages,
  currentPage,
  setCurrentPage,
  data,
  folderId,
  isSearchMode = false,
  onRetry,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const clearSearch = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("mode");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  const { sortedItems, currentSort, setCurrentSort } = useSortedItems(displayItems, "drive-sorted-items-config");

  // In search mode, use displayItems directly without sorting
  const itemsToDisplay = isSearchMode ? displayItems : sortedItems;

  const { selectedItemIds, handleItemSelection, clearSelection, handleToggleSelectAll } = useItemSelection({
    items: itemsToDisplay,
    currentPage,
    itemsPerPage,
    folderId,
  });
  const selectedFileItems = React.useMemo(
    () => itemsToDisplay.filter((item): item is FileItem => item.type === "file" && selectedItemIds.has(item.id)),
    [itemsToDisplay, selectedItemIds],
  );

  const {
    handleMoveItems,
    movingItemIds,
    isMoveFileLoading,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    showDeleteConfirmation,
    isDeleteLoading,
  } = useMoveDelete({
    items: itemsToDisplay,
    selectedItemIds,
    clearSelection,
  });

  // Wrapper function to handle sort change with proper typing
  const handleSortChange = React.useCallback(
    (sort: SortOption) => {
      setCurrentSort(sort);
    },
    [setCurrentSort],
  );

  const [currentFolder, parentFolder] = data ? createBreadCrumbs({ isRootDirectory, data }) : [null, null];

  return (
    <div data-testid="drive-view">
      {isMoveFileLoading && <TopBarProgress />}
      <DriveContent
        currentFolder={currentFolder}
        parentFolder={parentFolder}
        folderId={folderId}
        isLoading={isLoading}
        selectedItemsCount={selectedItemIds.size}
        selectedFileItems={selectedFileItems}
        onDeleteClick={handleDeleteClick}
        isDeleteLoading={isDeleteLoading}
        currentSort={currentSort}
        onSortChange={handleSortChange}
        items={itemsToDisplay}
        isRootFolder={isRootDirectory}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onMoveItem={handleMoveItems}
        movingItemIds={movingItemIds}
        selectedItemIds={selectedItemIds}
        onItemSelection={handleItemSelection}
        onClearSelection={clearSelection}
        onToggleSelectAll={handleToggleSelectAll}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        isSearchMode={isSearchMode}
        query={query}
        onClearSearch={clearSearch}
        error={error}
        onRetry={onRetry}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        itemCount={selectedItemIds.size}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
